/**
 * Rol de usuario.
 *
 * La base de datos guarda roles canónicos, pero por compatibilidad histórica
 * circulan alias (`manager`, `visualizador`, `admin`, `vista_cliente`).
 * Esta clase normaliza cualquier variante a un único valor canónico para que
 * el resto de la aplicación nunca tenga que compararlos a mano.
 */

export const ROLES_CANONICOS = ['superadmin', 'admin_cliente', 'tecnico', 'cliente_final'] as const;

export type RolCanonico = (typeof ROLES_CANONICOS)[number];

const ALIAS: Readonly<Record<string, RolCanonico>> = {
  admin: 'admin_cliente',
  manager: 'tecnico',
  visualizador: 'cliente_final',
  vista_cliente: 'cliente_final',
};

/** Mayor número = más privilegios. Permite comparaciones jerárquicas. */
const NIVEL: Readonly<Record<RolCanonico, number>> = {
  cliente_final: 1,
  tecnico: 2,
  admin_cliente: 3,
  superadmin: 4,
};

const ETIQUETA: Readonly<Record<RolCanonico, string>> = {
  superadmin: 'Superadministrador',
  admin_cliente: 'Administrador',
  tecnico: 'Técnico',
  cliente_final: 'Visualizador',
};

export class Rol {
  private constructor(
    readonly canonico: RolCanonico,
    /** Valor tal cual vino de la base de datos, para no perderlo al escribir. */
    readonly original: string,
  ) {}

  /** Un rol desconocido o ausente cae al mínimo privilegio. */
  static from(raw: string | null | undefined): Rol {
    const limpio = (raw ?? '').trim().toLowerCase();
    const canonico = (ROLES_CANONICOS as readonly string[]).includes(limpio)
      ? (limpio as RolCanonico)
      : (ALIAS[limpio] ?? 'cliente_final');
    return new Rol(canonico, raw ?? '');
  }

  static superadmin(): Rol {
    return new Rol('superadmin', 'superadmin');
  }

  get esSuperadmin(): boolean {
    return this.canonico === 'superadmin';
  }

  get esAdminCliente(): boolean {
    return this.canonico === 'admin_cliente';
  }

  get esTecnico(): boolean {
    return this.canonico === 'tecnico';
  }

  get esSoloLectura(): boolean {
    return this.canonico === 'cliente_final';
  }

  get nivel(): number {
    return NIVEL[this.canonico];
  }

  get etiqueta(): string {
    return ETIQUETA[this.canonico];
  }

  /** `true` si este rol tiene al menos tantos privilegios como `minimo`. */
  alMenos(minimo: RolCanonico): boolean {
    return this.nivel >= NIVEL[minimo];
  }

  esAlguno(...roles: RolCanonico[]): boolean {
    return roles.includes(this.canonico);
  }

  equals(otro: Rol): boolean {
    return this.canonico === otro.canonico;
  }

  toString(): string {
    return this.canonico;
  }
}
